import { useEffect, useState } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";

import { MobileHeader } from "../components/MobileHeader";
import { MobileCard } from "../components/MobileCard";
import { ApiError } from "../../api/client";
import { fetchSitePages, siteApi, type SitePageSummary } from "../../api/site";

function pageStatusLabel(status: string): string {
  switch (status) {
    case "publish": return "公開";
    case "draft": return "下書き";
    case "private": return "非公開";
    case "pending": return "承認待ち";
    case "future": return "予約済み";
    default: return status || "—";
  }
}

function formatJst(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(date);
}

export const MobileSitePage = () => {
  const [pages, setPages] = useState<SitePageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadPages() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSitePages();
      setPages(data.pages ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "固定ページの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }

  async function refreshPages() {
    setRefreshing(true);
    setError(null);
    try {
      await siteApi.refreshPages(true);
      await loadPages();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "固定ページの更新に失敗しました。");
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => { void loadPages(); }, []);

  return (
    <>
      <MobileHeader title="サイト管理" />
      <main className="mobile-content">
        <MobileCard title="固定ページ一覧">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button type="button" onClick={() => void refreshPages()} disabled={loading || refreshing}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <RefreshCw size={14} />
              {refreshing ? "更新中..." : "今すぐ更新"}
            </button>
          </div>
          {loading && <p>固定ページを読み込み中...</p>}
          {error && <p role="alert">{error}</p>}
          {!loading && !error && pages.length === 0 && <p>固定ページが見つかりません。</p>}
          {!loading && !error && pages.length > 0 && (
            <div>
              {pages.map((page) => (
                <article key={page.wordpress_page_id} style={{ borderBottom: "1px solid #e5e7eb", padding: "12px 0" }}>
                  <div style={{ fontWeight: 600 }}>{page.title || "—"}</div>
                  <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>
                    {pageStatusLabel(page.status)} · #{page.wordpress_page_id} · {formatJst(page.modified)}
                  </div>
                  {page.link && <a href={page.link} target="_blank" rel="noopener noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6 }}>
                    <ExternalLink size={13} /> WordPressで開く
                  </a>}
                </article>
              ))}
            </div>
          )}
        </MobileCard>
      </main>
    </>
  );
};
